import { saveLastAttemptedRoute } from '@/utils/route'
import type { RouteRecordRaw } from 'vue-router'
import { createRouter, createWebHistory } from 'vue-router'
import { setupRouterGuards } from './guards'
import { useCurrencyStore } from '@/stores/currencyStore'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),

  scrollBehavior(to, from, savedPosition) {
    // If user clicked back button, restore scroll position
    if (savedPosition) {
      return savedPosition
    }

    // If navigating to hash anchor
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth'
      }
    }

    // For tab changes within same page, don't scroll
    if (to.path === from.path && to.query.tab !== from.query.tab) {
      return false
    }

    // Default: scroll to top
    return { top: 0, behavior: 'smooth' }
  },

  routes: [
    // Home redirect
    {
      path: '/',
      name: 'home',
      redirect: { name: 'public-pool' }
    },

    // Single public pool route with query-based tabs
    {
      path: '/public-pool',
      name: 'public-pool',
      component: () => import('@/views/PublicPoolView.vue'),
      meta: {
        public: true,
        title: 'Public Pool',
        description: 'Mining pool statistics and information'
      }
    },

    // Password reset confirmation (from email link) - using /reset-password to avoid conflict with /user/:username
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/auth/NewPasswordView.vue'),
      meta: {
        guest: true,
        title: 'Reset Password',
        layout: 'auth'
      }
    },

    // Legacy redirect for old password reset links from backend emails
    {
      path: '/user/password',
      redirect: (to) => ({
        name: 'new-password',
        query: to.query
      })
    },

    {
      path: '/user/:username',
      name: 'user-profile',
      component: () => import('@/views/UserProfileView.vue'),
      meta: {
        public: true,
        title: 'User Statistics',
        description: 'Mining statistics for user'
      },
      props: (route: any) => {
        const currencyStore = useCurrencyStore()
        return {
          username: route.params.username,
          coin: route.query.coin || currencyStore.selectedCurrency
        }
      }
    },

    // Clean worker route structure
    {
      path: '/user/:username/:workername',
      name: 'worker-profile',
      component: () => import('@/views/WorkerProfileView.vue'),
      meta: {
        public: true,
        title: 'Worker Statistics',
        description: 'Mining statistics for worker'
      },
      props: (route: any) => {
        const currencyStore = useCurrencyStore()
        return {
          username: route.params.username,
          workername: route.params.workername,
          coin: route.query.coin || currencyStore.selectedCurrency
        }
      }
    },

    // Auth routes
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/auth/LoginView.vue'),
      meta: {
        guest: true,
        title: 'Login',
        layout: 'auth'
      },
      beforeEnter: (to, from, next) => {
        if (from.meta?.protected) {
          saveLastAttemptedRoute(from)
        }
        next()
      }
    },

    {
      path: '/signup',
      name: 'signup',
      component: () => import('@/views/auth/SignupView.vue'),
      meta: {
        guest: true,
        title: 'Sign Up',
        layout: 'auth'
      }
    },

    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/auth/ResetPassword.vue'),
      meta: {
        guest: true,
        title: 'Reset Password',
        layout: 'auth'
      }
    },

    {
      path: '/new-password',
      name: 'new-password',
      component: () => import('@/views/auth/NewPasswordView.vue'),
      meta: {
        guest: true,
        title: 'Set New Password',
        layout: 'auth'
      }
    },

    {
      path: '/confirm-email',
      name: 'confirm-email',
      component: () => import('@/views/auth/ConfirmEmailView.vue'),
      meta: {
        guest: true,
        title: 'Confirm Email',
        layout: 'auth'
      }
    },

    // 2FA verification
    {
      path: '/verify-2fa',
      name: 'verify-2fa',
      component: () => import('@/views/auth/Verify2FAView.vue'),
      meta: {
        auth: true,
        verify2fa: true,
        title: '2FA Verification',
        layout: 'auth'
      }
    },

    // Wallet confirmation (from email link)
    {
      path: '/user/activate_await_address',
      name: 'confirm-wallet',
      component: () => import('@/views/ConfirmWalletView.vue'),
      meta: {
        public: true,
        title: 'Confirm Wallet'
      }
    },

    // Other public routes
    {
      path: '/connect-wallet',
      name: 'connect-wallet',
      component: () => import('@/views/ConnectWalletView.vue'),
      meta: {
        public: true,
        title: 'Connect Wallet'
      }
    },

    {
      path: '/documentation',
      name: 'documentation',
      component: () => import('@/views/NotFoundView.vue'),
      meta: {
        public: true,
        title: 'Documentation'
      }
    },

    {
      path: '/contacts',
      name: 'contacts',
      component: () => import('@/views/NotFoundView.vue'),
      meta: {
        public: true,
        title: 'Contacts'
      }
    },

    {
      path: '/statistics',
      name: 'statistics',
      component: () => import('@/views/MyStatisticsView.vue'),
      meta: {
        protected: true,
        title: 'My Statistics',
        requiresAuth: true
      }
    },

    {
      path: '/profile',
      name: 'profile',
      component: () => import('@/views/MyStatisticsView.vue'),
      meta: {
        protected: true,
        title: 'Profile',
        requiresAuth: true
      }
    },

    {
      path: '/settings',
      name: 'settings',
      component: () => import('@/views/SettingsView.vue'),
      meta: {
        protected: true,
        title: 'Settings',
        requiresAuth: true
      }
    },
    // Legacy compatibility redirects
    {
      path: '/public-pool/search',
      redirect: (to) => ({
        name: 'public-pool',
        query: { ...to.query, tab: 'search' }
      })
    },
    {
      path: '/public-pool/user/:username',
      redirect: (to) => ({
        name: 'user-profile',
        params: to.params,
        query: to.query
      })
    },
    {
      path: '/public-pool/user/:username/worker/:workername',
      redirect: (to) => ({
        name: 'worker-profile',
        params: to.params,
        query: to.query
      })
    },

    // Catch-all route (404)
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFoundView.vue'),
      meta: {
        title: 'Page Not Found',
        public: true
      }
    }
  ] as RouteRecordRaw[]
})

// Sync currency from route before each navigation
router.beforeEach((to, _from, next) => {
  try {
    const currencyStore = useCurrencyStore()
    const coin = (to.query.coin as string) || ''

    // Skip currency sync for auth pages and other non-content pages
    const skipCurrencySync =
      to.meta?.guest === true ||
      to.meta?.verify2fa === true ||
      to.path.includes('/login') ||
      to.path.includes('/signup') ||
      to.path.includes('/reset-password') ||
      to.path.includes('/new-password') ||
      to.path.includes('/confirm-email') ||
      to.path.includes('/verify-2fa') ||
      to.path.includes('/connect-wallet') ||
      to.path.includes('/documentation') ||
      to.path.includes('/contacts')

    if (skipCurrencySync) {
      // Still initialize from storage for consistency, but don't add coin to URL
      currencyStore.initializeFromStorage()
      return next()
    }

    if (coin) {
      // If coin is in URL, sync store with it
      currencyStore.initializeFromRoute(coin)
    } else {
      // If no coin in URL, initialize from storage
      currencyStore.initializeFromStorage()

      // If store has currency different from default, add it to URL
      if (currencyStore.selectedCurrency && currencyStore.selectedCurrency !== 'BTC') {
        const newQuery = { ...to.query, coin: currencyStore.selectedCurrency }
        next({ ...to, query: newQuery })
        return
      }
    }
  } catch (_e) {
    // no-op; route will proceed regardless
  }
  next()
})

// Setup router guards
setupRouterGuards(router)

// Global navigation error handler
router.onError((error) => {
  console.error('Router error:', error)

  // Handle chunk loading errors (code splitting)
  if (error.message.includes('Loading chunk')) {
    window.location.reload()
  }
})

router.afterEach(async (to, from, failure) => {
  if (!failure) setTimeout(() => window.HSStaticMethods?.autoInit(), 100)
})

export default router
